import { createRouter, createWebHistory } from 'vue-router';

import loggerService from '@/services/logger.service';
import { setRouter, setLastViewedBroadcastId } from '@/utils/routerAccess';

const routes = [
  {
    path: '/',
    name: 'Home',
    meta: { requiresAuth: true }
  },
  {
    path: '/auth',
    components: {
      default: () => import('@/views/auth/AuthWrapper.vue')
    },
    children: [
      {
        path: '',
        name: `entry`,
        component: () => import('@/views/auth/entry.vue'),
        props: true
      },
      {
        path: 'embedded',
        name: `EmbeddedEntry`,
        component: () => import('@/views/auth/EmbeddedEntry.vue')
      },
      {
        path: 'differentuserloggedin',
        name: `DifferentUserLoggedIn`,
        component: () => import('@/views/auth/differentUserLoggedIn.vue'),
        props: true
      },
      {
        path: 'link/:email/:secretCode',
        name: `fromMagicLink`,
        component: () => import('@/views/auth/fromMagicLink.vue'),
        props: true
      },
      {
        path: 'confirmSignup',
        name: `confirmSignup`,
        component: () => import('@/views/auth/confirmSignup.vue'),
        props: true
      },
      {
        path: 'magiclinkSent',
        name: `magiclinkSent`,
        component: () => import('@/views/auth/magicLinkSent.vue'),
        props: true
      },
      {
        path: 'external',
        name: `external`,
        component: () => import('@/views/auth/external.vue'),
        props: true
      },
      {
        path: 'sso',
        name: 'sso',
        component: () => import('@/views/auth/SSOLogin.vue'),
        props: true
      },
      {
        path: 'recorder-success',
        name: 'RecorderLoginSuccess',
        component: () => import('@/views/auth/RecorderLoginSuccess.vue'),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/event-exit',
    name: 'EventExit',
    component: () => import('@/views/event/EventExit.vue'),
    props: true
  },
  {
    path: '/end-event',
    name: 'EndEvent',
    component: () => import('@/views/event/EndEvent.vue'),
    props: true
  },
  {
    // No auth required — internal debug/test page for error tracking verification
    path: '/metadata',
    name: 'MetadataPanel',
    component: () => import('@/views/MetadataPanel.vue')
  },
  {
    path: '/error/:code?',
    name: 'ErrorPage',
    component: () => import('@/views/404.vue'),
    meta: { requiresAuth: true },
    props: true
  },
  {
    path: '/error-403',
    name: 'ErrorPage403',
    component: () => import('@/views/403.vue'),
    meta: { requiresAuth: true },
    props: true
  },
  {
    name: 'MyProfile',
    path: '/profile',
    component: () => import('@/views/Profile.vue')
  },
  {
    name: 'FreeTrialEventCreatedSuccess',
    path: '/free-trial/:eventID',
    meta: { requiresAuth: true },
    component: () => import('../views/free-trial/success.vue')
  },
  {
    name: 'EventSplash',
    path: '/splash/:eventID',
    meta: { requiresAuth: true },
    components: {
      default: () => import('../views/event/SplashPage.vue'),
      bottomfixed: () => import('@/views/BottomDrawerSubView.vue')
    }
  },
  {
    name: 'RecordingExit',
    path: '/recording-exit/:eventID',
    meta: { requiresAuth: true },
    components: {
      default: () => import('../views/event/RecordingExit.vue'),
      bottomfixed: () => import('@/views/BottomDrawerSubView.vue')
    }
  },
  {
    name: 'RecordingUploadStatus',
    path: '/recording-upload-status/:eventID',
    meta: { requiresAuth: true },
    components: {
      default: () => import('../views/event/RecordingUploadStatus.vue'),
      bottomfixed: () => import('@/views/BottomDrawerSubView.vue')
    }
  },
  {
    name: 'HlsTester',
    path: '/hls_tester',
    meta: { requiresAuth: false },
    component: () => import('../views/HLSTester.vue')
  },
  {
    path: '/e/:eventID',
    components: {
      default: () => import('@/views/event/EventWrapper.vue'),
      notifications: () => import('@/views/event/NotificationsSubView.vue'),
      bottomfixed: () => import('@/views/BottomDrawerSubView.vue')
    },
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'EventLanding' }
      },
      {
        name: 'EventLanding',
        path: 'redirect',
        component: () => import('@/views/event/LandingPageDecider.vue'),
        props: true
      },
      stageRoute(
        {
          path: 'broadcast/:id',
          name: 'Broadcast',
          props: true,
          component: () => import('@/views/Broadcast.vue')
        },
        'Broadcast'
      ),
      stageRoute(
        {
          path: '',
          name: 'stageRoll',
          props: true,
          component: () => import('@/views/event/StageRoll.vue')
        },
        'stageRoll'
      ),
      {
        path: 'discussion-room/:id',
        name: 'DiscussionRoom',
        props: true,
        component: () => import('../views/DiscussionRoom.vue')
      },
      {
        name: 'EventBooth',
        path: 'eventbooth/:boothID',
        component: () => import('@/views/event/EventBoothView.vue')
      },
      {
        name: 'EventBooth2',
        path: 'eventbooth2/:boothID',
        component: () => import('@/views/event/EventBoothView.vue')
      },
      {
        name: 'Profile',
        path: 'profile',
        component: () => import('@/views/event/EventUserProfile.vue')
      },
      {
        name: 'EventOnBoarding',
        path: 'onboarding',
        component: () => import('@/views/event/Onboarding.vue')
      },
      {
        path: 'livenesscontrol',
        name: 'LivenessControl',
        component: () => import('@/views/LivenessControl.vue')
      },
      {
        path: 'speakertechsetup',
        name: 'SpeakerTechSetup',
        component: () => import('@/views/event/TechSetup.vue')
      },
      {
        path: 'attendeetechsetup',
        name: 'AttendeeTechSetup',
        component: () => import('@/views/event/TechSetup.vue')
      }
    ]
  },
  {
    name: 'UngatedTechCheck',
    path: '/techcheck',
    meta: { requiresAuth: false },
    component: () => import('@/views/UngatedTechCheck.vue')
  },
  {
    path: '/:catchAll(.*)',
    redirect: {
      name: 'ErrorPage',
      params: {
        code: '404'
      }
    },
    meta: { requiresAuth: true }
  }
];

// Needs to be fixed. Each name in the children should be prefixed with route.name
// and all navigations should be done to unique names instead of the complex logic in
// module/stage/actions#handleSubMenuClick
function stageRoute({ path, children = [], meta = {}, ...route }, prefix) {
  return {
    ...route,
    path: `stage/${path}`,
    meta: {
      ...meta,
      prefix: prefix,
      title: 'Stage'
    },
    children: [
      ...children,
      {
        name: `${prefix}-DiscussionGroups`,
        path: 'discussiongroups',
        component: () => import('@/views/event/DicussionRoomGridView.vue'),
        meta: {
          title: 'Discussion Room'
        }
      },
      {
        name: `${prefix}-EventBoothsGridPage`,
        path: 'eventbooths',
        component: () => import('@/views/event/EventBoothsGridView.vue'),
        meta: {
          title: 'Booths'
        }
      },
      {
        name: `${prefix}-EventBoothsGridPage2`,
        path: 'eventbooths2',
        component: () => import('@/views/event/EventBoothsGridView.vue'),
        meta: {
          title: 'Booths 2'
        }
      },
      {
        name: `${prefix}-EventNetwork`,
        path: 'network',
        component: () => import('@/views/event/EventNetwork.vue'),
        meta: {
          title: 'Network'
        }
      },
      {
        name: `${prefix}-EventAgendaPage`,
        path: 'agenda',
        component: () => import('@/views/event/EventAgendaPage.vue'),
        meta: {
          title: 'Agenda'
        }
      },
      {
        name: `${prefix}-Speaker`,
        path: 'speaker',
        component: () => import('@/views/event/Speaker.vue'),
        meta: {
          title: 'Speakers'
        }
      },
      {
        name: `${prefix}-EmbeddedWidget`,
        path: 'embedded/:widgetId',
        component: () => import('@/views/EmbeddedWidget.vue')
      }
    ]
  };
}

const scrollBehavior = () => {
  const element = document.getElementById('stageWrapper_subPage');
  if (element) {
    element.scrollTop = 0;
  }
};

const router = createRouter({
  history: createWebHistory(import.meta.env.MODE === 'production' ? '' : ''),
  // mode: 'history',
  routes,
  scrollBehavior
});

setRouter(router);

router.afterEach(async (to, from) => {
  loggerService.info('Route changed called! will try to update room', to, from);
  if (to.path.includes('/e/') || to.path.includes('/splash/')) {
    window.sessionStorage.setItem('gcLastRoutePath', to.path);
  }
  const { sendRoomUpdateEvent } = await import('@/store/modules/socketMessageProcessor');
  sendRoomUpdateEvent();

  if (from.params.eventID !== to.params.eventID) {
    if (
      window.configs.BETA_LOCKED_EVENTS &&
      window.configs.BETA_LOCKED_EVENTS.includes(to.params.eventID) &&
      window.configs.IS_BETA_ENV !== 'yes'
    ) {
      loggerService.info('Reloading because locked event is being opened without beta code!');
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }
});

router.beforeEach(async (to, from, next) => {
  const { default: store } = await import('@/store');

  const preFixTitle = to.meta.title ? `${to.meta.title} | ` : '';
  const eventName = store.state.event.event.title || '';
  if (preFixTitle && eventName) {
    document.title = preFixTitle + eventName;
  }

  if (from.name === 'Broadcast') {
    setLastViewedBroadcastId(from.params.id);
  }

  if (to.params.eventID && to.query.magicTokenId && to.query.magicTokenVerificationId) {
    const { default: store } = await import('@/store');
    if (store.state.event.cventWebinarIntegration) {
      const { oauthLoadingState } = await import('@/state/oauthLoading');
      const { getEnv } = await import('@/constants');
      oauthLoadingState.active = true;
      oauthLoadingState.error = null;
      next(false);

      const baseUrl = getEnv('VUE_APP_BACKEND_SERVER').replace(/\/+$/, '');
      const url = new URL(`${baseUrl}/event/${to.params.eventID}/oauth2/authorize`);
      url.searchParams.set('magicTokenId', String(to.query.magicTokenId));
      url.searchParams.set('magicTokenVerificationId', String(to.query.magicTokenVerificationId));
      try {
        const res = await fetch(url.href, { method: 'GET', mode: 'cors', cache: 'no-cache' });
        const data = await res.json();
        if (res.ok && data.redirectUrl) {
          globalThis.location.href = data.redirectUrl;
          return;
        }
        loggerService.error('OAuth authorize failed', {
          status: res.status,
          message: data.message,
          eventID: to.params.eventID
        });
        oauthLoadingState.error = data.message || 'Something went wrong. Please try again.';
        oauthLoadingState.active = false;
      } catch (err) {
        loggerService.error('OAuth authorize request error', {
          error: err?.message || err,
          eventID: to.params.eventID
        });
        oauthLoadingState.error = 'Something went wrong. Please try again.';
        oauthLoadingState.active = false;
      }
      return;
    }
  }

  if (to.name === 'EmbeddedEntry') {
    next();
  } else if (store.state.user.currentUser) {
    if (
      to.name === 'fromMagicLink' ||
      to.name === 'DifferentUserLoggedIn' ||
      to.name === 'sso' ||
      to.name === 'EmbeddedEntry' ||
      to.name === 'RecorderLoginSuccess' ||
      to.name === 'entry' ||
      to.name === 'magiclinkSent'
    ) {
      next();
    } else if (to.fullPath.startsWith('/auth') || to.fullPath === '/') {
      let path = '';
      if (to.query.eventID) {
        path = `/splash/${to.query.eventID}`;
      } else {
        path = '/userhome';
      }
      next(path);
    }
  } else if (to.name === 'sso' || (from.name == 'sso' && to.fullPath.startsWith('/splash'))) {
    next();
  } else if (to.name === 'entry' && !to.query.eventID) {
    next('/error/404');
  } else if (to.name === 'EventExit') {
    next();
  } else {
    if (to.fullPath.startsWith('/auth/external') && Object.keys(to.params).length === 0) {
      let path = '/auth';
      if (to.query.eventID) {
        path += `?eventID=${to.query.eventID}`;
        next(path);
      }
    } else if (to.matched.some(record => record.meta.requiresAuth)) {
      if (to.name === 'ErrorPage' || to.name === 'ErrorPage403') {
        next();
        return;
      }

      store.dispatch('RESET_USER');
      let path = '/auth';
      if (from.params.eventID) {
        path += `?eventID=${from.params.eventID}`;
      } else if (to.params.eventID) {
        path += `?eventID=${to.params.eventID}`;
      }
      next(path);
    }
  }

  function getAnalyticData(path) {
    const trackingPathName = path.name;
    switch (trackingPathName) {
      case 'Broadcast':
        return { id: path.params.id, name: 'BROADCAST' };

      case 'DiscussionRoom':
        return { id: path.params.id, name: 'DISCUSSION_GROUP' };

      case 'EventBooth':
      case 'EventBooth2':
        return { id: path.params.boothID, name: 'BOOTH' };

      case 'EventLanding':
      case 'EventSplash':
      case 'SpeakerSetup':
      case 'SpeakerTestSetup':
      case 'EventOnBoarding':
        return { id: path.params.eventID, name: 'EVENT' };

      case 'EventAgendaPage':
      case 'DiscussionGroups':
      case 'EventBoothsGridPage':
      case 'EventBoothsGridPage2':
      case 'EventNetwork':
      case 'stageRoll':
      case 'Speaker': {
        if (path.params?.id) {
          return { id: path.params.id, name: 'BROADCAST' };
        }
        return { id: path.params.eventID, name: 'EVENT' };
      }
      default:
        return { id: null, name: null };
    }
  }

  const fromPathData = getAnalyticData(from);
  const toPathData = getAnalyticData(to);

  const { default: analytics } = await import('@/analytics');
  analytics.track('Route change', {
    from: from.fullPath,
    to: to.fullPath,
    event_id: to.params?.eventID || null,
    to_entity_id: toPathData?.id || null,
    to_entity_type: toPathData?.name || null,
    from_entity_id: fromPathData?.id || null,
    from_entity_type: fromPathData?.name || null
  });

  next();
});

export default router;
